'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';

export type TransitionPhase =
  | 'idle'
  | 'disintegrate'
  | 'ant-reveal'
  | 'done';

type TransitionContextValue = {
  phase: TransitionPhase;
  startSwarmTransition: () => void;
};

const TransitionCtx = createContext<TransitionContextValue>({
  phase: 'idle',
  startSwarmTransition: () => {},
});

const PHASE_DURATIONS: Record<Exclude<TransitionPhase, 'idle'>, number> = {
  disintegrate: 900,
  'ant-reveal': 800,
  done: 1300,
};

export function TransitionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<TransitionPhase>('idle');
  const router = useRouter();
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const cleanup = useCallback(() => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startSwarmTransition = useCallback(() => {
    if (phase !== 'idle') return;
    cleanup();

    setPhase('disintegrate');

    const d = PHASE_DURATIONS.disintegrate;
    const ar = PHASE_DURATIONS['ant-reveal'];
    const dn = PHASE_DURATIONS.done;

    const t1 = setTimeout(() => setPhase('ant-reveal'), d);
    const t2 = setTimeout(() => router.push('/swarm'), d + 300);
    const t3 = setTimeout(() => setPhase('done'), d + ar);
    const t4 = setTimeout(() => setPhase('idle'), d + ar + dn);

    timers.current = [t1, t2, t3, t4];
  }, [phase, router, cleanup]);

  return (
    <TransitionCtx.Provider value={{ phase, startSwarmTransition }}>
      {children}
    </TransitionCtx.Provider>
  );
}

export function useTransition() {
  return useContext(TransitionCtx);
}
